'use client'

import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useProductByIdQuery } from '@/generated/graphql'
import { Button } from '@/General/Button'
import { Price } from '@/General/Price'
import { useAddItemToCart } from '@/useAddItemToCart'
import { toastEmitter } from '@/General/Toast'
import {
  getCustomAddToCartFlowMetadata,
  type CustomAddToCartFlowProps,
} from '@/lib/config/customAddToCartFlows'
import {
  vorstBaseSwatchUrl,
  vorstInsertSwatchUrl,
} from '@/lib/config/vorstBoschSwatchImages'
import { useStorefrontMarket } from '@/useStorefrontMarket'

// --- constants (aligned with legacy AddVorstBosch.aspx.cs) ---
const INSERT_PRODUCT_ID = 19140
const SPECIAL_EDITION_PAD_PRODUCT_ID = 19216

const STEP = { BASE: 0, INSERTS: 1, ENGRAVING: 2 } as const

type StepIndex = (typeof STEP)[keyof typeof STEP]

/** GraphQL `url` is often stored without a leading slash. */
function productPageHrefFromApiUrl(
  url: string | null | undefined
): string | null {
  if (!url?.trim()) return null
  return `/${url.replace(/^\/+|\/+$/g, '')}`
}

function BackToProductLink({
  productUrl,
  variant,
}: {
  productUrl: string | null | undefined
  variant: 'header' | 'muted'
}) {
  const href = productPageHrefFromApiUrl(productUrl)
  if (!href) return null
  const className =
    variant === 'header'
      ? 'inline-block text-sm font-semibold text-white/90 hover:text-white mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-vi-gray rounded-sm'
      : 'inline-block text-sm font-semibold text-gray-700 hover:text-gray-900 mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2 rounded-sm'
  return (
    <Link href={href} className={className}>
      ← Back to product
    </Link>
  )
}

type Option = {
  id: number | null | undefined
  description: string | null | undefined
  position: number
  price: number
  salePrice?: number | null
  inStock: boolean
  currency?: { symbol?: string | null } | null
}

function getProductIdFromSearchParams(
  searchParams: Record<string, string | string[] | undefined>
): number | null {
  const raw = searchParams?.productId
  if (raw == null) return null
  const single = Array.isArray(raw) ? raw[0] : raw
  const n = parseInt(single, 10)
  return Number.isFinite(n) ? n : null
}

function initialStepForProduct(productId: number | null): StepIndex {
  if (productId === SPECIAL_EDITION_PAD_PRODUCT_ID) return STEP.INSERTS
  return STEP.BASE
}

/** Engraved SKUs are cart-only (legacy position ≥ 100); also filter by name if position is wrong in API. */
function isEngravedInsertDescription(
  description: string | null | undefined
): boolean {
  if (!description) return false
  return description.endsWith(' - Engraved')
}

function effectiveOptionPrice(o: Option): number {
  const p = o.price
  const s = o.salePrice
  if (s != null && s < p) return s
  return p
}

type VorstSwatchTileProps = {
  label: string
  imageUrl: string | null
  selected: boolean
  currencySymbol: string
  price: number
  salePrice?: number | null
  /** Inserts hide per-tile price (first insert is free; line-item prices are misleading). */
  showPricing?: boolean
  onSelect: () => void
}

function VorstSwatchTile({
  label,
  imageUrl,
  selected,
  currencySymbol,
  price,
  salePrice,
  showPricing = true,
  onSelect,
}: VorstSwatchTileProps) {
  const displayPrice =
    salePrice != null && salePrice < price ? salePrice : price
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={label}
      className={[
        'relative aspect-square min-h-[100px] w-full rounded border-2 overflow-hidden text-left transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-vi-red focus-visible:ring-offset-2',
        selected
          ? 'border-vi-red ring-2 ring-vi-red ring-offset-2'
          : 'border-gray-300 hover:border-gray-400',
        imageUrl
          ? 'bg-neutral-900'
          : 'bg-gray-50 flex flex-col items-center justify-center p-3',
      ].join(' ')}
      style={
        imageUrl
          ? {
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined
      }
    >
      {imageUrl ? (
        <>
          <span
            className="absolute top-2 left-2 right-2 font-semibold text-white leading-tight"
            style={{
              fontSize: '0.7em',
              textShadow: '0 1px 3px rgba(0,0,0,0.95)',
            }}
          >
            {label}
          </span>
          {showPricing ? (
            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-2 py-1.5 text-xs font-semibold text-white tabular-nums">
              {currencySymbol}
              {Number(displayPrice).toFixed(2)}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <span className="font-medium text-center text-sm text-gray-800 block">
            {label}
          </span>
          {showPricing ? (
            <Price
              price={price}
              salePrice={salePrice}
              currencySymbol={currencySymbol}
              size="sm"
            />
          ) : null}
        </>
      )}
    </button>
  )
}

export function VorstBoschAddToCart({
  flowSlug,
  searchParams,
}: CustomAddToCartFlowProps) {
  const router = useRouter()
  const flowMeta = getCustomAddToCartFlowMetadata(flowSlug)
  const storefrontMarket = useStorefrontMarket()
  const brandCode = storefrontMarket.brandCode ?? 'VANISHING_INC_US'
  const productId = getProductIdFromSearchParams(searchParams)

  const isSpecialEdition = productId === SPECIAL_EDITION_PAD_PRODUCT_ID
  const [step, setStep] = useState<StepIndex>(() =>
    initialStepForProduct(productId)
  )

  const [selectedBaseOptionId, setSelectedBaseOptionId] = useState<
    number | null
  >(null)
  const [selectedInsertOptionIds, setSelectedInsertOptionIds] = useState<
    number[]
  >([])
  const [engravingInsertOptionIds, setEngravingInsertOptionIds] = useState<
    Set<number>
  >(new Set())

  const padProductQuery = useProductByIdQuery(
    { id: productId ?? 0, brandCode },
    { enabled: Boolean(productId) }
  )
  const insertProductQuery = useProductByIdQuery(
    { id: INSERT_PRODUCT_ID, brandCode },
    { enabled: true }
  )

  const padProduct = padProductQuery.data?.ecommProductById
  const insertProduct = insertProductQuery.data?.ecommProductById
  const padOptions = (padProduct?.options ?? []) as Option[]
  const allInsertOptions = useMemo(
    () => (insertProduct?.options ?? []) as Option[],
    [insertProduct?.options]
  )
  /** Colour inserts only — exclude engraved rows (position ≥ 100 in DB, or “ - Engraved” suffix). */
  const insertOptions = useMemo(
    () =>
      allInsertOptions.filter(
        o =>
          (o.position ?? 0) < 100 && !isEngravedInsertDescription(o.description)
      ),
    [allInsertOptions]
  )

  const baseOptions = padOptions
  const needsBasePick = baseOptions.length > 0
  const currencySymbol =
    padOptions[0]?.currency?.symbol ?? insertOptions[0]?.currency?.symbol ?? '$'

  /** Per extra insert — from catalog (same source as cart line items). */
  const extraInsertUnitPrice = useMemo(() => {
    const ref = insertOptions[0]
    if (!ref) return null
    return effectiveOptionPrice(ref)
  }, [insertOptions])

  /** Engraving fee = engraved option price minus matching colour (matches legacy 125 − 50 = 75). */
  const engravingUpcharge = useMemo(() => {
    const base = insertOptions[0]
    if (!base?.description) return null
    const engraved = allInsertOptions.find(
      o => o.description === `${base.description} - Engraved`
    )
    if (!engraved) return null
    const delta = effectiveOptionPrice(engraved) - effectiveOptionPrice(base)
    return delta > 0 ? delta : null
  }, [insertOptions, allInsertOptions])

  const pricingReady =
    extraInsertUnitPrice != null &&
    Number.isFinite(extraInsertUnitPrice) &&
    extraInsertUnitPrice > 0 &&
    engravingUpcharge != null &&
    Number.isFinite(engravingUpcharge) &&
    engravingUpcharge > 0

  const insertsFooterSummary = useMemo(() => {
    if (isSpecialEdition) return null
    const baseOpt = baseOptions.find(o => o.id === selectedBaseOptionId)
    if (!baseOpt) return null
    const price = baseOpt.salePrice ?? baseOpt.price ?? 0
    return `${baseOpt.description ?? 'Base'} base (${currencySymbol}${Number(price).toFixed(2)})`
  }, [baseOptions, selectedBaseOptionId, currencySymbol, isSpecialEdition])

  const engravingFooterLines = useMemo(() => {
    const lines: string[] = []
    if (!pricingReady || extraInsertUnitPrice == null) return lines
    if (!isSpecialEdition && selectedBaseOptionId) {
      const baseOpt = baseOptions.find(o => o.id === selectedBaseOptionId)
      if (baseOpt) {
        const price = baseOpt.salePrice ?? baseOpt.price ?? 0
        lines.push(
          `${baseOpt.description ?? 'Base'} base (${currencySymbol}${Number(price).toFixed(2)})`
        )
      }
    }
    const n = selectedInsertOptionIds.length
    if (n === 0) {
      lines.push('0 inserts selected')
    } else {
      lines.push('1 insert (included)')
      const extra = Math.max(0, n - 1)
      lines.push(
        `${extra} extra insert${extra === 1 ? '' : 's'} (${currencySymbol}${Number(extraInsertUnitPrice).toFixed(2)} each)`
      )
    }
    return lines
  }, [
    isSpecialEdition,
    selectedBaseOptionId,
    baseOptions,
    currencySymbol,
    selectedInsertOptionIds.length,
    extraInsertUnitPrice,
    pricingReady,
  ])

  const { addItem, isPending } = useAddItemToCart({
    onError: err => {
      toastEmitter.emit('toast', {
        title: err instanceof Error ? err.message : 'Could not add to cart',
        variant: 'error',
      })
    },
  })

  const toggleInsert = useCallback((optionId: number) => {
    setSelectedInsertOptionIds(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    )
  }, [])

  const toggleEngraving = useCallback((insertOptionId: number) => {
    setEngravingInsertOptionIds(prev => {
      const next = new Set(prev)
      if (next.has(insertOptionId)) next.delete(insertOptionId)
      else next.add(insertOptionId)
      return next
    })
  }, [])

  const findEngravedOption = useCallback(
    (baseDescription: string | null | undefined) => {
      if (!baseDescription) return null
      const engraved = allInsertOptions.find(
        o => o.description === `${baseDescription} - Engraved`
      )
      return engraved?.id ?? null
    },
    [allInsertOptions]
  )

  const goNextFromBase = useCallback(() => {
    if (needsBasePick && selectedBaseOptionId == null) {
      toastEmitter.emit('toast', {
        title: 'Please select a base style for your pad.',
        variant: 'error',
      })
      return
    }
    setStep(STEP.INSERTS)
  }, [needsBasePick, selectedBaseOptionId])

  const goNextFromInserts = useCallback(() => {
    if (!isSpecialEdition && selectedInsertOptionIds.length === 0) {
      toastEmitter.emit('toast', {
        title: 'Please select at least one insert for your close-up pad.',
        variant: 'error',
      })
      return
    }
    setStep(STEP.ENGRAVING)
  }, [isSpecialEdition, selectedInsertOptionIds.length])

  const goBack = useCallback(() => {
    if (step === STEP.INSERTS && isSpecialEdition) return
    setStep(s => Math.max(STEP.BASE, s - 1) as StepIndex)
  }, [step, isSpecialEdition])

  const handleAddToCart = useCallback(async () => {
    if (!padProduct?.id || !productId) return
    if (needsBasePick && !isSpecialEdition && selectedBaseOptionId == null) {
      toastEmitter.emit('toast', {
        title: 'Please choose a base for your pad.',
        variant: 'error',
      })
      return
    }
    try {
      const baseOptionIdNum =
        !isSpecialEdition && selectedBaseOptionId != null
          ? selectedBaseOptionId
          : undefined

      await addItem({
        productId: padProduct.id,
        productOptionId: baseOptionIdNum ?? undefined,
        quantity: 1,
      })

      let freeInsertGiven = isSpecialEdition
      for (const insertOptionId of selectedInsertOptionIds) {
        const opt = insertOptions.find(o => o.id === insertOptionId)
        const engravedId = opt ? findEngravedOption(opt.description) : null
        const useEngraved =
          engravedId != null && engravingInsertOptionIds.has(insertOptionId)
        const optionId = useEngraved ? engravedId : insertOptionId
        const description = freeInsertGiven ? undefined : 'Included with pad'
        await addItem({
          productId: INSERT_PRODUCT_ID,
          productOptionId: optionId ?? undefined,
          description: description ?? undefined,
          quantity: 1,
        })
        freeInsertGiven = true
      }

      router.push('/checkout')
    } catch {
      // Error already surfaced via useAddItemToCart onError toast
    }
  }, [
    padProduct,
    productId,
    isSpecialEdition,
    needsBasePick,
    selectedBaseOptionId,
    selectedInsertOptionIds,
    insertOptions,
    engravingInsertOptionIds,
    findEngravedOption,
    addItem,
    router,
  ])

  if (productId == null) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center px-4">
        <p className="text-gray-600 text-center">
          Missing product. Open this page from the product with{' '}
          <code className="text-sm">?productId=…</code>.
        </p>
      </div>
    )
  }

  if (
    padProductQuery.isLoading ||
    insertProductQuery.isLoading ||
    !padProduct ||
    !insertProduct
  ) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col">
        <div className="h-40 md:h-48 bg-vi-gray animate-pulse px-4 py-10 md:py-14">
          <div className="max-w-(--breakpoint-xl) mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {flowMeta?.title ?? 'Configure your pad'}
            </h1>
          </div>
        </div>
        <div className="flex-1 flex justify-center items-center py-16">
          <div className="rounded-lg bg-gray-200 h-12 w-64 animate-pulse" />
        </div>
      </div>
    )
  }

  if (!pricingReady) {
    return (
      <div className="w-full min-h-[50vh] flex flex-col items-center justify-center px-4 bg-gray-100 py-12">
        <div className="max-w-md w-full text-center">
          <BackToProductLink productUrl={padProduct.url} variant="muted" />
          <p className="text-red-700 text-lg leading-relaxed">
            Pad insert pricing is missing or incomplete (no usable insert
            options or engraved pairs in the catalogue). Configuration cannot
            continue.
          </p>
        </div>
      </div>
    )
  }

  const extraInsert = extraInsertUnitPrice
  const engraving = engravingUpcharge

  const flowTitle = flowMeta?.title ?? 'Configure your pad'
  const flowDescription =
    flowMeta?.description ??
    'Choose inserts and optional engraving. Handmade Vorst & Bosch close-up pads.'
  const headerSubtitle = flowDescription
    ? `${flowTitle} — ${flowDescription}`
    : flowTitle

  return (
    <div className="flex w-full min-h-[min(100dvh,1200px)] flex-col bg-gray-100">
      <header className="bg-vi-gray text-white py-10 md:py-14 shrink-0 shadow-md w-full">
        <div className="max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0">
          <BackToProductLink productUrl={padProduct.url} variant="header" />
          <p className="text-gray-300 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-2">
            Vorst &amp; Bosch
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            {padProduct.name}
          </h1>
          <p className="mt-3 text-base md:text-lg text-white/88 max-w-3xl leading-relaxed">
            {headerSubtitle}
          </p>
        </div>
      </header>

      <main className="flex-1 w-full max-w-(--breakpoint-xl) mx-auto px-4 xl:px-0 py-8 md:py-12">
        {/* Step: base (hidden for limited edition — legacy starts on inserts) */}
        {step === STEP.BASE && !isSpecialEdition && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              Step 1: Choose your base
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              First, choose which style of base you&apos;d like for your
              close-up pad.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4 mb-10">
              {baseOptions.map(opt => (
                <VorstSwatchTile
                  key={opt.id ?? opt.description}
                  label={opt.description ?? 'Option'}
                  imageUrl={vorstBaseSwatchUrl(opt.description)}
                  selected={selectedBaseOptionId === opt.id}
                  currencySymbol={opt.currency?.symbol ?? currencySymbol}
                  price={opt.price}
                  salePrice={opt.salePrice}
                  onSelect={() => setSelectedBaseOptionId(opt.id ?? null)}
                />
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                color="green"
                onClick={goNextFromBase}
                disabled={needsBasePick && selectedBaseOptionId == null}
              >
                Next: choose an insert
              </Button>
            </div>
          </section>
        )}

        {step === STEP.INSERTS && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              {isSpecialEdition
                ? 'Would you like extra inserts?'
                : 'Step 2: Choose your pad inserts'}
            </h2>
            <p className="text-gray-700 mb-8 leading-relaxed">
              {isSpecialEdition
                ? `You may add additional inserts to your pad for just ${currencySymbol}${Number(extraInsert).toFixed(2)} each.`
                : `Please select color inserts for your pad (your first insert is free, and additional inserts are ${currencySymbol}${Number(extraInsert).toFixed(2)} each).`}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
              {insertOptions.map(opt => (
                <VorstSwatchTile
                  key={opt.id ?? opt.description}
                  label={opt.description ?? 'Insert'}
                  imageUrl={vorstInsertSwatchUrl(opt.description)}
                  selected={
                    opt.id != null && selectedInsertOptionIds.includes(opt.id)
                  }
                  currencySymbol={opt.currency?.symbol ?? currencySymbol}
                  price={opt.price}
                  salePrice={opt.salePrice}
                  showPricing={false}
                  onSelect={() => opt.id != null && toggleInsert(opt.id)}
                />
              ))}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-4">
              <span className="text-sm text-gray-500 min-h-[1.25rem]">
                {insertsFooterSummary ?? '\u00A0'}
              </span>
              <div className="flex gap-2 justify-end">
                {!(step === STEP.INSERTS && isSpecialEdition) && (
                  <Button color="gray" onClick={goBack}>
                    Back
                  </Button>
                )}
                <Button color="green" onClick={goNextFromInserts}>
                  Final step: engraving
                </Button>
              </div>
            </div>
          </section>
        )}

        {step === STEP.ENGRAVING && (
          <section className="bg-white p-6 md:p-10 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              {isSpecialEdition
                ? 'Engrave your extra inserts?'
                : 'Step 3: Engraving'}
            </h2>
            <p className="text-gray-700 border-b border-gray-200 pb-4 mb-6 leading-relaxed">
              Would you like to engrave your insert with a logo or name? Each
              insert costs {currencySymbol}
              {Number(engraving).toFixed(2)} to engrave. Check each colour
              you&apos;d like to engrave:
            </p>
            <div className="space-y-3 mb-6">
              {selectedInsertOptionIds.length === 0 ? (
                <p className="text-gray-600">
                  No inserts selected — we&apos;ll add the pad only. You can
                  still continue below.
                </p>
              ) : (
                selectedInsertOptionIds.map(insertOptionId => {
                  const opt = insertOptions.find(o => o.id === insertOptionId)
                  const engravedId = opt
                    ? findEngravedOption(opt.description)
                    : null
                  if (!opt || !engravedId) return null
                  return (
                    <label
                      key={insertOptionId}
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition"
                    >
                      <input
                        type="checkbox"
                        checked={engravingInsertOptionIds.has(insertOptionId)}
                        onChange={() => toggleEngraving(insertOptionId)}
                        className="rounded border-gray-300 text-vi-red focus:ring-vi-red"
                      />
                      <span className="text-gray-800">{opt.description}</span>
                    </label>
                  )
                })
              )}
            </div>
            <p className="text-gray-600 text-sm mb-8 border-b border-gray-200 pb-4 leading-relaxed">
              Inserts are engraved using our laser engraving technology. Please
              email us your logo or text after checkout.
              {isSpecialEdition
                ? ' The special edition surface cannot be engraved — only additional pads.'
                : ''}
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
              <div className="text-sm text-gray-500 space-y-0.5">
                {engravingFooterLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button color="gray" onClick={goBack}>
                  Back
                </Button>
                <Button
                  color="green"
                  onClick={handleAddToCart}
                  disabled={isPending}
                >
                  {isPending ? 'Adding…' : 'Add to cart'}
                </Button>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
